import { useCallback, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { RefreshCw, Download, ChevronDown, FileSpreadsheet } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { apiClient } from '@/api/client'
import { useReportRange } from '@/hooks/useReportRange'
import { DateRangeFilter } from '@/components/admin/reports/DateRangeFilter'
import type { AdvancedStockReport } from '@/types'
import * as Help from '@/lib/inventory-report-help'
import { MetricHint, type CountTarget } from './primitives'
import { StockNowCard } from './StockNowCard'
import { ActivityCard } from './ActivityCard'
import { LossesCard } from './LossesCard'
import { NeedsAttention } from './NeedsAttention'

// The exportable inventory reports, each downloadable on its own. The
// consumption_variance slug is kept for old links; the sheet it produces is
// the Count Results report.
const INVENTORY_REPORTS: { slug: string; label: string }[] = [
  { slug: 'stock', label: 'Stock on hand' },
  { slug: 'purchase_orders', label: 'Purchase orders' },
  { slug: 'sales', label: 'Sales (by item / channel)' },
  { slug: 'waste', label: 'Waste & spoilage' },
  { slug: 'consumption_variance', label: 'Count results' },
  { slug: 'vendor_aging', label: 'Vendor aging (AP)' },
  { slug: 'vendor_ledger', label: 'Vendor ledger' },
]

/**
 * Store Inventory → Reports (F-FE-N99 rewrite).
 *
 * One date range drives everything on this page AND the Excel export — the
 * screen and the workbook can never describe different windows. The page is
 * four questions in plain language; the section components own their copy.
 */
export function ReportsTab({ onCountItem, onOpenAlerts }: {
  onCountItem: (item: CountTarget) => void
  onOpenAlerts: () => void
}) {
  const range = useReportRange('last_30')
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState<string | null>(null)

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['advancedStockReport', range.fromISO, range.toISO],
    queryFn: () => apiClient.getAdvancedStockReport(range.fromISO, range.toISO),
    staleTime: 60_000,
  })
  const report: AdvancedStockReport | undefined = data?.data

  const handleExport = useCallback(async (sheet?: string) => {
    setExportError(null)
    setExporting(true)
    try {
      await apiClient.exportInventoryReportsXlsx(range.fromISO, range.toISO, sheet)
    } catch (e) {
      setExportError(e instanceof Error && e.message ? e.message : 'Export failed. Please try again.')
    } finally {
      setExporting(false)
    }
  }, [range.fromISO, range.toISO])

  return (
    <div className="space-y-6 pb-12">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
        <div className="space-y-1.5 max-w-3xl">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold">Inventory reports</h3>
            <MetricHint text={Help.REPORTS_TAB_INTRO} label="inventory reports" />
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed">
            What your stock is worth, what you used, what you lost, and what to do next.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <DateRangeFilter range={range} />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-9" disabled={exporting}>
                <Download className="w-4 h-4 mr-1.5" />
                {exporting ? 'Exporting…' : 'Export to Excel'}
                <ChevronDown className="w-3.5 h-3.5 ml-1.5 opacity-70" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => handleExport('all')}>
                <FileSpreadsheet className="w-4 h-4 mr-2" /> All reports (one workbook)
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs">Export a single report</DropdownMenuLabel>
              {INVENTORY_REPORTS.map(r => (
                <DropdownMenuItem key={r.slug} onClick={() => handleExport(r.slug)}>
                  {r.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          {exportError && <span className="basis-full text-xs text-red-600">{exportError}</span>}
        </div>
      </div>

      {isLoading && (
        <div className="text-center py-16 text-muted-foreground">
          <RefreshCw className="w-8 h-8 mx-auto mb-3 animate-spin opacity-50" />
          <p className="font-medium">Loading your report…</p>
        </div>
      )}

      {isError && !isLoading && (
        <div className="text-center py-16 text-muted-foreground">
          <p className="font-medium">Couldn’t load the report.</p>
          <Button variant="outline" size="sm" className="mt-3" onClick={() => refetch()}>Try again</Button>
        </div>
      )}

      {report && !isLoading && (
        <>
          <NeedsAttention attention={report.attention} onCountItem={onCountItem} onOpenAlerts={onOpenAlerts} />
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-start">
            <StockNowCard stockNow={report.stock_now} onCountItem={onCountItem} />
            <ActivityCard activity={report.activity} />
          </div>
          <LossesCard losses={report.losses} />
        </>
      )}
    </div>
  )
}
